import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { LANG_URL_PARAM } from '../util/locale';
import { USER_TYPE } from '../constants/user';
import { isMarketplaceRespare, isMarketplaceSecondLife } from '../util/marketplace';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const HowItWorksPage = loadable(() => import(/* webpackChunkName: "HowItWorksPage" */ '../containers/InfoPages/HowItWorksPage/HowItWorksPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/InfoPages/PrivacyPolicyPage/PrivacyPolicyPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/InfoPages/TermsOfServicePage/TermsOfServicePage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/InfoPages/AboutPage/AboutPage'));
const ListingBulkUploadPage = loadable(() => import(/* webpackChunkName: "ListingBulkUploadPage" */ '../containers/ListingBulkUploadPage/ListingBulkUploadPage'));
const MembersAdminPage = loadable(() => import(/* webpackChunkName: "MembersAdminPage" */ '../containers/MembersAdminPage/MembersAdminPage'));
const LeaseImportPage = loadable(() => import(/* webpackChunkName: "LeaseImportPage" */ '../containers/LeaseImportPage/LeaseImportPage'));
const CompaniesPage = loadable(() => import(/* webpackChunkName: "CompaniesPage" */ '../containers/CompaniesPage/CompaniesPage'));
const MembersPage = loadable(() => import(/* webpackChunkName: "MembersPage" */ '../containers/MembersPage/MembersPage'));
const SearchPageSellAsIs = loadable(() => import(/* webpackChunkName: "SearchPageSellAsIs" */ '../containers/SearchPageSellAsIs/SearchPageSellAsIs'));
const SearchPageMyListings = loadable(() => import(/* webpackChunkName: "SearchPageMyListings" */ '../containers/SearchPageMyListings/SearchPageMyListings'));
const SearchPagePublished = loadable(() => import(/* webpackChunkName: "SearchPagePublished" */ '../containers/SearchPagePublished/SearchPagePublished'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (config) => {
  const layoutConfig = config.layout;

  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  let routes = [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    {
      path: '/contact',
      name: "ContactPage",
      component: ContactPage,
    },
    {
      path: '/sell-as-is',
      name: 'SearchPageSellAsIs',
      auth: true,
      authPage: 'LoginPage',
      component: SearchPageSellAsIs,
      loadData: pageDataLoadingAPI.SearchPageSellAsIs.loadData,
    },
    {
      path: '/my-listings',
      name: 'SearchPageMyListings',
      auth: true,
      authPage: 'LoginPage',
      component: SearchPageMyListings,
      loadData: pageDataLoadingAPI.SearchPageMyListings.loadData,
    },
    {
      path: '/published',
      name: 'SearchPagePublished',
      component: SearchPagePublished,
      loadData: pageDataLoadingAPI.SearchPagePublished.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/bulk-upload',
      name: 'NewListingBulkUploadPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect
          name="ListingBulkUploadPage"
          params={{  id: '0', tab: 'general' }}
        />
      ),
    },
    {
      path: '/bulk-upload/:id/:tab',
      name: 'ListingBulkUploadPage',
      auth: true,
      component: ListingBulkUploadPage,
      loadData: pageDataLoadingAPI.ListingBulkUploadPage.loadData,
    },
    {
      path: '/lease-import',
      name: 'LeaseImportPage',
      auth: true,
      component: LeaseImportPage,
      loadData: pageDataLoadingAPI.LeaseImportPage.loadData,
    },
    {
      path: '/companies',
      name: 'CompaniesPage',
      auth: true,
      roles: [USER_TYPE.ADMIN, USER_TYPE.HQ_DASHBOARD_VIEWER],
      component: CompaniesPage,
      loadData: pageDataLoadingAPI.CompaniesPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'general' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/users',
      name: 'MembersAdminPage',
      auth: true,
      authPage: 'LoginPage',
      roles: [USER_TYPE.ADMIN, USER_TYPE.HQ_DASHBOARD_VIEWER],
      component: MembersAdminPage,
      loadData: pageDataLoadingAPI.MembersAdminPage.loadData,
    },
    {
      path: '/users/dealers',
      name: 'DealersAdminPage',
      auth: true,
      authPage: 'LoginPage',
      roles: [USER_TYPE.ADMIN, USER_TYPE.HQ_DASHBOARD_VIEWER],
      extraProps: { isDealers: true },
      component: MembersAdminPage,
      loadData: (params, ...rest) => 
        pageDataLoadingAPI.MembersAdminPage.loadData({ ...params, isDealers: true }, ...rest),
    },
    {
      path: '/company-settings',
      name: 'MembersPage',
      auth: true,
      authPage: 'LoginPage',
      roles: [USER_TYPE.COMPANY_DEALER],
      component: MembersPage,
      loadData: pageDataLoadingAPI.MembersPage.loadData,
    },
    {
      path: '/infos',
      name: 'InfosPage',
      component: () => <NamedRedirect name="AboutPage"/>,
    },
    {
      path: '/infos/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      path: '/infos/how-it-works',
      name: 'HowItWorksPage',
      component: HowItWorksPage,
    },
    {
      path: '/infos/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },
    {
      path: '/infos/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ].map(route => ({...route, path: `/${LANG_URL_PARAM}${route.path}` }));


  if (isMarketplaceSecondLife(config)) {
    const blacklistedRoutes = ['NewListingPage', 'NewListingBulkUploadPage', 'ListingBulkUploadPage', 'InboxBasePage', 'InboxPage'];

    routes = routes.filter(route => !blacklistedRoutes.includes(route.name));
  } else if (isMarketplaceRespare(config)) {
    const blacklistedRoutes = ['MembersAdminPage', 'DealersAdminPage', 'LeaseImportPage', 'CompaniesPage', 'SearchPageSellAsIs', 'SearchPagePublished'];

    routes = routes.filter(route => !blacklistedRoutes.includes(route.name));
  }

  return routes;
};

export default routeConfiguration;
